import React, { useState } from 'react'
import { observer } from "mobx-react";
import { useStores } from 'src/stores/rootStore';
import { EmployeeReviewActions } from 'src/stores/models/enums';
import { ReviewEmployeeAccountDto } from 'src/stores/models/qarFormModels';
import { Box, Button, CircularProgress, DialogActions, DialogContent, Fab } from '@material-ui/core';
import { Save, Check, Clear } from '@material-ui/icons';
import { Skeleton } from '@material-ui/lab';
import { QARFeedBack } from './feedback';
import api from 'src/utils/api';


export const QARFormSave = observer(() => {

    const { qarFormStore, uiStateStore } = useStores();

    const [loading, setLoading] = useState(false);
    const [success, setSuccess] = useState<boolean | undefined>();

    const handleSaveChanges = async () => {

        uiStateStore.setModalOpen(false);
        uiStateStore.setModalContent(undefined);

        setSuccess(undefined);
        setLoading(true);
        
        let shouldShowFeedback = qarFormStore.willComplete;
        let inBulkReview = qarFormStore.shouldDisplayBulkReview;
        const response = await qarFormStore.saveReview();
        if (!api.responseOk(response)) {

            if(inBulkReview) {
                uiStateStore.pushMessage("The saving process might take a bit longer than usual.", "warning", true);
                uiStateStore.pushMessage("Please, try refreshing the form in a few minutes.", "warning", true);
                return;
            }
            
            setLoading(false);
            setSuccess(false);            
            return;            

        }

        if (response.data.isSuccessful === false) {
            setLoading(false);
            setSuccess(false)

            // display errors if any have been gathered
            response.data.errorMessages?.forEach((errorMessage: string) => {
                uiStateStore.pushMessage(errorMessage, 'error', true);
            })

            return;
        }

        setSuccess(true);
        setLoading(false);

        uiStateStore.pushMessage("Changes were saved successfully.", "success");

        qarFormStore.employeesChanges?.flatMap(e => e.accounts).forEach(a => (a.action.valid = true));
        qarFormStore.delegatedUnmatchedAccountsChanges?.flatMap(e => e.accounts).forEach(a => (a.action.valid = true));

        if (shouldShowFeedback) {
            setTimeout(() => {
                uiStateStore.setModalContent(<QARFeedBack />)
                uiStateStore.setModalOpen(true);
            }, 1E3)

        }

    }

    const cancelSave = () => {
        uiStateStore.setModalOpen(false);
        uiStateStore.setModalContent(undefined);
    }


    const confirmSave = async () => {

        if (!qarFormStore.hasChanges) {
            uiStateStore.pushMessage("No changes to save.", 'warning');
            return;
        }

        const invalidEmployeesComments = qarFormStore.employeesChanges?.flatMap(e => e.accounts).filter(a => (a.action.name === EmployeeReviewActions.Change || a.action.name === EmployeeReviewActions.Revoked) && (!a.action.comment)) || [];
        const invalidDelegatedAccountsComments = qarFormStore.delegatedUnmatchedAccountsChanges?.flatMap(e => e.accounts).filter(a => (a.action.name === EmployeeReviewActions.Change || a.action.name === EmployeeReviewActions.Revoked) && (!a.action.comment)) || [];

        if (invalidEmployeesComments.length > 0 || invalidDelegatedAccountsComments.length > 0) {
            uiStateStore.pushMessage("Some of your employee reviews require additional information.", 'warning');

            qarFormStore.employeesChanges?.flatMap(e => e.accounts).filter(a => ((a.action.name === EmployeeReviewActions.Change || a.action.name === EmployeeReviewActions.Revoked) && a.action.comment) || a.action.name === EmployeeReviewActions.MissingAction).forEach(a => (a.action.valid = true));
            qarFormStore.delegatedUnmatchedAccountsChanges?.flatMap(e => e.accounts).filter(a => ((a.action.name === EmployeeReviewActions.Change || a.action.name === EmployeeReviewActions.Revoked) && a.action.comment) || a.action.name === EmployeeReviewActions.MissingAction).forEach(a => (a.action.valid = true));

            invalidEmployeesComments.forEach(a => a.action.valid = false);
            invalidDelegatedAccountsComments.forEach(a => a.action.valid = false);

            setSuccess(false);

            return;
        }

        qarFormStore.employeesChanges!.forEach((employee) => {
            employee.accounts.forEach((value) => {
                if ((value.action.name === EmployeeReviewActions.Change || value.action.name === EmployeeReviewActions.Revoked) &&
                    !(value.action.comment && value.action.comment.trim() !== "") && !(value.action.ticketId && value.action.ticketId.trim() !== "")) {
                    value.action.valid = false;
                }
                else {
                    value.action.valid = true;
                }
            })
        });

        qarFormStore.delegatedUnmatchedAccountsChanges!.forEach((unmatchedAccount) => {
            unmatchedAccount.accounts.forEach((value) => {
                if ((value.action.name === EmployeeReviewActions.Change || value.action.name === EmployeeReviewActions.Revoked) &&
                    !(value.action.comment && value.action.comment.trim() !== "") && !(value.action.ticketId && value.action.ticketId.trim() !== "")) {
                    value.action.valid = false;
                }
                else {
                    value.action.valid = true;
                }
            })
        });

        if (qarFormStore.selectedQuarter && new Date(qarFormStore.selectedQuarter.reviewDeadline) < new Date()) {
            qarFormStore.employeesChanges!.forEach((value) => {
                value.accounts.forEach((value) => {
                    if (value.action.name === EmployeeReviewActions.MissingAction) {
                        value.action.valid = false;
                    }
                })
            });

            qarFormStore.delegatedUnmatchedAccountsChanges!.forEach((value) => {
                value.accounts.forEach((value) => {
                    if (value.action.name === EmployeeReviewActions.MissingAction) {
                        value.action.valid = false;
                    }
                })
            });
        }

        if (qarFormStore.employeesChanges!.flatMap(x => x.accounts).some((value) => value.action.valid === false) ||
            qarFormStore.delegatedUnmatchedAccountsChanges!.flatMap(x => x.accounts).some((value) => value.action.valid === false)) {
            uiStateStore.pushMessage("Please fill all the required fields.", 'warning');
            return;
        }

        uiStateStore.setModalContent(renderConfirmationDialogContent())
        uiStateStore.setModalOpen(true)
    }

    const renderConfirmationDialogContent = () => {

        const approveAccounts = [];
        const revokeAccounts = [];
        const changeAccounts = [];
        const delegateAccounts = [];
        const missingActionAccounts = [];

        const accountChanges = qarFormStore.accountChanges;
        accountChanges.forEach((x: ReviewEmployeeAccountDto) => {

            switch (x.action.name) {
                case EmployeeReviewActions.Approved:
                    approveAccounts.push(x); break;
                case EmployeeReviewActions.Revoked:
                    revokeAccounts.push(x); break;
                case EmployeeReviewActions.Change:
                    changeAccounts.push(x); break;
                case EmployeeReviewActions.DelegatedTo:
                    delegateAccounts.push(x); break;
                case EmployeeReviewActions.MissingAction:
                    missingActionAccounts.push(x); break;
            }

        });

        const unmatchedAccountChanges = qarFormStore.unmatchedAccountChanges;
        unmatchedAccountChanges.forEach((x: ReviewEmployeeAccountDto) => {

            switch (x.action.name) {
                case EmployeeReviewActions.Approved:
                    approveAccounts.push(x); break;
                case EmployeeReviewActions.Revoked:
                    revokeAccounts.push(x); break;
                case EmployeeReviewActions.Change:
                    changeAccounts.push(x); break;
                case EmployeeReviewActions.DelegatedTo:
                    delegateAccounts.push(x); break;
                case EmployeeReviewActions.MissingAction:
                    missingActionAccounts.push(x); break;
            }

        });

        return (
            // TODO: uncomment once the logic for creating Jira tickets is ready
            // <div>
            //     <h2>Are you sure?</h2>
            //     <DialogContent dividers={true}>
            //         {approveAccounts.length > 0 && <p>You have marked {approveAccounts.length} account{approveAccounts.length === 1 ? "" : "s"} as approved.</p>}
            //         {revokeAccounts.length > 0 && <p>You have marked {revokeAccounts.length} account{revokeAccounts.length === 1 ? "" : "s"} as revoked and
            //             <b>{revokeAccounts.length === 1 ? " a" : ""} JIRA ticket{revokeAccounts.length === 1 ? "" : "s"} will be created for {revokeAccounts.length === 1 ? "it" : "each one"}</b>.</p>}
            //         {changeAccounts.length > 0 && <p>You have marked {changeAccounts.length} account{changeAccounts.length === 1 ? "" : "s"} as changed and
            //             <b>{changeAccounts.length === 1 ? " a" : ""} JIRA ticket{changeAccounts.length === 1 ? "" : "s"} will be created for {changeAccounts.length === 1 ? "it" : "each one"}</b>.</p>}
            //         {delegateAccounts.length > 0 && <p>You are about to delegate {delegateAccounts.length} account{delegateAccounts.length === 1 ? "" : "s"} to another manager.</p>}
            //         <p>Please note, that changes are final and you won't be able to modify these accounts later.</p>
            //     </DialogContent>
            //     <DialogActions>
            //         <Button onClick={cancelSave} color="primary">
            //             Cancel
            //         </Button>
            //         <Button onClick={handleSaveChanges} color="primary">
            //             Yes
            //         </Button>
            //     </DialogActions>
            // </div>

            <div>
                <h2>Are you sure?</h2>
                <DialogContent dividers={true}>
                    {approveAccounts.length > 0 && <p>You have marked {approveAccounts.length} account{approveAccounts.length === 1 ? "" : "s"} as approved.</p>}

                    {
                        revokeAccounts.length > 0 && 
                        <p>
                            You have marked {revokeAccounts.length} account{revokeAccounts.length === 1 ? "" : "s"} as revoked.<br />
                            Ticket{revokeAccounts.length === 1 ? "" : "s"} will be created for each revoked account in the corresponding system.
                        </p>
                    }

                    {
                        changeAccounts.length > 0 && 
                        <p>
                            You have marked {changeAccounts.length} account{changeAccounts.length === 1 ? "" : "s"} as change.<br />
                            Ticket{changeAccounts.length === 1 ? "" : "s"} will be created for each changed account in the corresponding system.
                        </p>
                    }

                    {delegateAccounts.length > 0 && <p>You are about to delegate {delegateAccounts.length} account{delegateAccounts.length === 1 ? "" : "s"} to another manager.</p>}
                    {missingActionAccounts.length > 0 && <p>You have removed the action{missingActionAccounts.length === 1 ? "" : "s"} for {missingActionAccounts.length} account{missingActionAccounts.length === 1 ? "" : "s"}.</p>}
                    {qarFormStore.selectedQuarter && new Date(qarFormStore.selectedQuarter.reviewDeadline) < new Date() && <p>Please note, that changes are final and you won't be able to modify these accounts later.</p>}
                </DialogContent>
                <DialogActions>
                    <Button onClick={cancelSave} color="primary">
                        Cancel
                    </Button>
                    <Button onClick={handleSaveChanges} color="primary">
                        Yes
                    </Button>
                </DialogActions>
            </div>
        );
    }

    const renderProgress = () => {

        if (loading) {
            return <CircularProgress />;
        }

        let allAccounts = 0;
        let reviewedAccounts = 0;

        qarFormStore.employeesChanges!.forEach((value) => {
            allAccounts += value.accounts.length;
            reviewedAccounts += value.accounts.filter((value) => value.action.name !== EmployeeReviewActions.MissingAction).length;
        });

        if (qarFormStore.delegatedUnmatchedAccountsChanges && qarFormStore.delegatedUnmatchedAccountsChanges?.length > 0) {
            qarFormStore.delegatedUnmatchedAccountsChanges!.forEach((value) => {
                allAccounts += value.accounts.length;
                reviewedAccounts += value.accounts.filter((value) => value.action.name !== EmployeeReviewActions.MissingAction).length;
            });
        }

        let progressPercentage = (allAccounts > 0) ? Math.round(reviewedAccounts / allAccounts * 100) : 0;

        return (
            <Box position="relative" display="inline-flex">
                <CircularProgress value={progressPercentage} variant="determinate" size={56} thickness={4.5} />
                <Box
                    top={0}
                    left={0}
                    bottom={0}
                    right={0}
                    position="absolute"
                    display="flex"
                    alignItems="center"
                    justifyContent="center"
                >
                    {reviewedAccounts}/{allAccounts}
                </Box>
            </Box>
        );
    }

    const getSaveButtonClassName = () => {
        if (success === undefined) {
            return "qar-form-save-button";
        }

        return success ?
            "qar-form-save-button-success" :
            "qar-form-save-button-error";
    }

    const getSaveButtonComponent = () => {
        if (success === undefined) {
            return <Save />
        }

        return success ?
            <Check /> :
            <Clear />
    }

    const renderSave = () => {

        if (qarFormStore.selectedQuarter?.isManagerReviewAdminCompleted) {
            return <></>
        }

        if (qarFormStore.employeesChanges === undefined) {

            if (success !== undefined) {
                setSuccess(undefined)
            }

            return (
                <div className="qar-form-save-root saveForm">
                    <div className="qar-form-save-wrapper">
                        <Skeleton variant="circle" width={50} height={50} />
                    </div>
                    <div className="qar-form-save-wrapper">
                        <Skeleton variant="circle" width={50} height={50} />
                    </div>
                    <div className="qar-form-save-wrapper">
                        <Skeleton variant="rect" width={150} height={30} />
                    </div>
                </div>
            );

        }

        return (
            <div className="qar-form-save-root saveForm">
                <div className="qar-form-save-wrapper">
                    {renderProgress()}
                </div>
                <div className="qar-form-save-wrapper">
                    <Fab
                        aria-label="save"
                        color="primary"
                        className={getSaveButtonClassName()}
                        onClick={confirmSave}
                    >
                        {getSaveButtonComponent()}
                    </Fab>
                    {loading && <CircularProgress size={68} className="qar-form-save-fab-progress" />}
                </div>
                <div className="qar-form-save-wrapper">
                    <Button
                        variant="contained"
                        color="primary"
                        className={getSaveButtonClassName()}
                        disabled={loading}
                        onClick={confirmSave}
                    >
                        Save
                    </Button>
                    {loading && <CircularProgress size={24} className="qar-form-save-button-progress" />}
                </div>
            </div>
        );
    }

    return (
        <>
            {renderSave()}
        </>
    );

});